import { useState } from 'react'
import { HelpModal } from './HelpModal'

interface Props {
  /** Short single-line summary shown inside the modal under the field label. */
  content: string
  /** Longer beginner-friendly explanation rendered as markdown in the modal. */
  help?: string | undefined
  /** Field label (used as the modal's header). */
  label?: string | undefined
  /** Accessibility label for the help icon button. */
  ariaLabel?: string | undefined
}

/**
 * Click-to-open help icon. The "i" button beside a field label opens a modal
 * with the short hint + a longer help body when one is supplied. The previous
 * hover-tooltip behavior was too thin to fit beginner-friendly explanations.
 *
 * The component keeps the same `content` prop the rest of the app uses, so
 * existing call sites pick up the modal upgrade for free.
 */
export function HelpTooltip({ content, help, label, ariaLabel }: Props) {
  const [open, setOpen] = useState(false)

  return (
    <>
      <button
        type="button"
        aria-label={ariaLabel ?? 'Help'}
        aria-expanded={open}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          setOpen(true)
        }}
        data-testid="help-icon"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '1.1rem',
          height: '1.1rem',
          marginLeft: '0.35rem',
          borderRadius: '50%',
          border: '1px solid var(--border)',
          background: 'var(--bg)',
          color: 'var(--ink-700)',
          fontSize: '0.75rem',
          fontWeight: 'bold',
          cursor: 'pointer',
          padding: 0,
          // A serif lowercase "i" clipped in the 1.1rem circle and read like an
          // underscore — sans italic + tight line height keeps the glyph whole.
          fontFamily: 'inherit',
          fontStyle: 'italic',
          lineHeight: 1,
          flexShrink: 0,
        }}
      >
        i
      </button>
      <HelpModal
        open={open}
        onClose={() => setOpen(false)}
        label={label ?? (ariaLabel ? ariaLabel.replace(/^Help for /, '') : 'Help')}
        hint={content}
        help={help}
      />
    </>
  )
}
